import { useContext, useMemo } from 'react';

import { UNIT_MEASURE, WATERFALL_ICON, WATERFALL_ID, WATERFALL_LABEL } from '@common/constants';
import { isWarrantyProduct } from '@common/helpers';
import {
    ApplianceModel,
    ColorModel,
    DrawingShape,
    EdgeModel,
    ImagePreviewModel,
    OptionalProduct,
    PricedProduct,
    ProductCatalogMap,
    ProductDetails,
    SelectedItemMap,
} from '@common/models';
import { COMING_SOON_IMAGE, getProductDescription } from '@common/utils';
import ImageUtils from '@core/utils/ImageUtils';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useEdges } from '@ee-api/edges-api/useEdges';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import {
    DrawingShapeContext,
    useOptionalProductContext,
    usePricedProductsContext,
    useProductDetailsContext,
} from '@ee-context';
import { useCanvasClone } from '@fabricCanvas/useCanvasClone';
import { useAppSelector } from '@redux/hooks';
import { selectSelectedFaucets } from '@redux/slices/faucetSlice';

import useFaucets from '../scrollable-area/faucets/useFaucets';

export interface UseGalleryImagesProps {
    isHidden: boolean;
}

export interface UseGalleryImagesReturn {
    previewImages: ImagePreviewModel[];
}

const emptyArray = [] satisfies ImagePreviewModel[];
const GALLERY_IMAGE_SIZE = 600; //preferred image size for the gallery

type GalleryProducts = ApplianceModel | EdgeModel | PricedProduct | OptionalProduct | ColorModel | ProductDetails;

interface GalleryPrices {
    unitCost?: number;
    unitCostWithoutDiscount?: number;
}

const galleryPrices = (product: GalleryProducts | undefined): GalleryPrices | undefined => {
    const discountedPrice = !!product && 'price' in product ? product.price : undefined;
    const regularPrice = !!product && 'priceWithoutDiscount' in product ? product.priceWithoutDiscount : undefined;
    return { unitCost: discountedPrice, unitCostWithoutDiscount: regularPrice ?? undefined }; // converts null to undefined = dumb hack
};

const galleryImage = (product: GalleryProducts | undefined) => {
    if (!product) {
        return COMING_SOON_IMAGE;
    }
    const beautyShotUrl = 'beautyShotImageUrl' in product ? product.beautyShotImageUrl : undefined;
    const imageUrl = 'imageUrl' in product ? product.imageUrl : undefined;
    return beautyShotUrl ?? imageUrl ?? COMING_SOON_IMAGE;
};

const productsToImageModels = (products: GalleryProducts[]): ImagePreviewModel[] => {
    return products.map((product: GalleryProducts) => {
        return {
            image: ImageUtils.imgUrl(galleryImage(product), GALLERY_IMAGE_SIZE),
            productName: getProductDescription(product),
            unitOfMeasure: product.unitOfMeasure,
            ...galleryPrices(product),
        } as ImagePreviewModel;
    });
};

const makeApplianceImages = (drawingShapes: DrawingShape[], catalog: ProductCatalogMap) => {
    // if catalog isProductCatalogMap or ProductDetails, customer supplied sinks become cutout
    const allAppliances = drawingShapes.flatMap((drawingShape) => {
        return drawingShape.appliances.map((appliance) => {
            const pp = catalog.get(appliance.applianceModel.productId);
            return (
                ({
                    ...pp,
                    imageUrl: appliance.applianceModel.imageUrl,
                    beautyShotImageUrl: appliance.applianceModel.beautyShotImageUrl,
                } as ProductDetails) ?? appliance.applianceModel
            );
        });
    });

    // remove duplicate productId items
    const appliances = allAppliances.filter(
        (appliance, index, array) => array.findIndex((element) => element.productId === appliance.productId) === index
    );
    return productsToImageModels(appliances);
};

function makeFaucetImages(selectedFaucets: SelectedItemMap, browsedFaucets: ApplianceModel[]) {
    // faucets are becoming more like other Appliances
    if (!selectedFaucets.size) {
        return emptyArray;
    }
    return productsToImageModels(browsedFaucets.filter((faucet) => selectedFaucets.get(faucet.productId)?.quantity));
}

const makeEdgeImages = (edges: EdgeModel[], pricedProducts: PricedProduct[]) => {
    return productsToImageModels(
        pricedProducts.reduce((acc, item) => {
            const edge = edges.find((element) => item.productId === element.productId);
            return edge ? [...acc, edge] : acc;
        }, [] as EdgeModel[])
    );
};

const makeWaterfallImages = (pricedProducts: PricedProduct[]) => {
    const waterFall = pricedProducts.find((item) => item.lineItemId === WATERFALL_ID);
    return waterFall
        ? [
              {
                  image: ImageUtils.imgUrl(WATERFALL_ICON ?? COMING_SOON_IMAGE, GALLERY_IMAGE_SIZE),
                  productName: getProductDescription(waterFall) ?? WATERFALL_LABEL,
                  unitCost: undefined,
                  unitCostWithoutDiscount: undefined,
                  unitOfMeasure: undefined,
              } as ImagePreviewModel,
          ]
        : emptyArray;
};

const makeWarrantyImages = (optionalProducts: OptionalProduct[], selectedOptionalProducts: SelectedItemMap) => {
    return productsToImageModels(
        optionalProducts.filter((product) => {
            if (!isWarrantyProduct(product)) {
                return false;
            }
            const foundProduct = selectedOptionalProducts.get(product.productId);
            return foundProduct?.isSelected && foundProduct?.quantity > 0;
        })
    );
};

export const useGalleryImages = (): UseGalleryImagesReturn => {
    const { canvasJpegUrl } = useCanvasClone(false);

    const { sortedEdges: edges } = useEdges();
    const selectedFaucets = useAppSelector(selectSelectedFaucets);
    const { optionalProducts } = useOptionalItems();
    const { browsedFaucets } = useFaucets();
    const { productCatalog } = useProductDetailsContext();

    const { pricedProducts } = usePricedProductsContext();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { drawingShapes } = useContext(DrawingShapeContext);
    const { selectedOptionalProducts } = useOptionalProductContext();

    const previewImages = useMemo((): ImagePreviewModel[] => {
        const previewAppliances = makeApplianceImages(drawingShapes, productCatalog);
        const previewFaucets = makeFaucetImages(selectedFaucets, browsedFaucets);
        const previewEdges = makeEdgeImages(edges, pricedProducts);
        const previewWaterfall = makeWaterfallImages(pricedProducts);
        const previewWarranty = makeWarrantyImages(optionalProducts, selectedOptionalProducts);
        const previewDesignAndSlab: ImagePreviewModel[] = [
            { image: canvasJpegUrl ?? '' },
            {
                image: ImageUtils.imgUrl(
                    selectedColor?.slabImageUrl
                        ? selectedColor.slabImageUrl.replace('-thumbnail', '')
                        : COMING_SOON_IMAGE,
                    GALLERY_IMAGE_SIZE
                ),
                productName: selectedColor ? getProductDescription(selectedColor) : undefined,
                ...galleryPrices(selectedColor),
                unitOfMeasure: UNIT_MEASURE.MATERIALS,
            },
            {
                image: ImageUtils.imgUrl(
                    selectedColor?.beautyShotImageUrl
                        ? selectedColor.beautyShotImageUrl.replace('-thumbnail', '')
                        : COMING_SOON_IMAGE,
                    GALLERY_IMAGE_SIZE
                ),
                productName: selectedColor ? getProductDescription(selectedColor) : undefined,
                ...galleryPrices(selectedColor),
                unitOfMeasure: UNIT_MEASURE.MATERIALS,
            },
        ];
        return [
            ...previewDesignAndSlab,
            ...previewAppliances,
            ...previewFaucets,
            ...previewEdges,
            ...previewWaterfall,
            ...previewWarranty,
        ];
    }, [
        drawingShapes,
        selectedFaucets,
        browsedFaucets,
        edges,
        pricedProducts,
        optionalProducts,
        selectedOptionalProducts,
        canvasJpegUrl,
        selectedColor,
    ]);

    return { previewImages };
};
